import {
    IconAddMoreCircleRegular,
    IconPenRegular,
    Text1,
    Text2,
    skinVars,
    BoxedRow,
    Tag,
    IconEmailRegular,
    IconCalendarEuroRegular,
    IconMobileDeviceRegular,
} from '@telefonica/mistica';
import React, { useEffect, useRef } from 'react';
import './index.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../utils/store/features/user';
import { formatMobileNumber } from '../../../utils/dataTransformation/unitsConversion';
import { ETypeUser } from '../../../utils/store/model/store.model';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { getCustomerData } from '../../../services/CustomerService';
import useUserlight from '../../../utils/hooks/useUserLight';
import Stopper from '../../../components/Stopper';
import { managementEnvironment } from '../../../utils/dataTransformation/environments';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import HeaderProfile from '../../../components/HeaderProfile';
import { useQuery } from '@tanstack/react-query';
import useGlobalEscalation from '../../../utils/globals/GlobalEscalation';
import { isIOS } from 'react-device-detect';

export const PersonalData: React.FC = () => {
    const isLight = useUserlight();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const typeUser = new URLSearchParams(window.location.search).get('clientType');
    const { trackingScreenView } = new TrackingEvents();
    const { checkEscalation } = useGlobalEscalation();
    const { data: personalData } = useQuery(['customer-data'], getCustomerData);

    const personalDataDisabled = managementEnvironment().REACT_APP_STOPPER_PROFILE_CUSTOMER_DATA_DISABLED;
    const refFirstLoad = useRef<boolean>(true);
    const reftiemout = useRef<number>(0);

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }

        refFirstLoad.current = false;
        return () => {
            refFirstLoad.current = true;

            window.clearTimeout(reftiemout.current);
        };
    }, []);

    return personalDataDisabled ? (
        <Stopper></Stopper>
    ) : (
        <div className='personal-data'>
            <HeaderProfile title='personalDataTitle2' breadcrumbs={[{ title: t('profileTitle'), url: '/profile' }]} />

            <div className='personal-data__content'>
                <div className='personal-data__content-titleContact'>
                    <Text1 medium color={skinVars.colors.textSecondary}>
                        {t('personalDataTitle')}
                    </Text1>
                </div>

                <div className='personal-data__content-boxedRow'>
                    <BoxedRow
                        headline={
                            <Tag type={personalData?.data.contactNumber?.verified ? 'success' : 'inactive'}>
                                {personalData?.data.contactNumber?.verified
                                    ? t('contactVerificationStep1Verified')
                                    : t('contactVerificationStep1NotVerified')}
                            </Tag>
                        }
                        asset={<IconMobileDeviceRegular color={skinVars.colors.brand} size={24} />}
                        title={t('personalDataMobile')}
                        subtitle={
                            personalData?.data.contactNumber?.value &&
                            formatMobileNumber(personalData?.data.contactNumber?.value)
                        }
                        extra={
                            !personalData?.data.contactNumber?.value && (
                                <Text2 medium color={skinVars.colors.textLink}>
                                    {t('personalDataAddMobile')}
                                </Text2>
                            )
                        }
                        right={
                            typeUser !== ETypeUser.PRECLIENT && (
                                <div
                                    className='personal-data__content-boxedRow__icon'
                                    onClick={() => {
                                        if (isLight) {
                                            dispatch(showSnackbar());
                                        } else {
                                            if (isIOS) {
                                                const newWindow = window.open('', 'newWindow');
                                                sessionStorage.setItem('newWindow', newWindow?.name || '');
                                            }
                                            checkEscalation('/update-personal-data', 'mobile', {
                                                mobileVerified: personalData?.data.contactNumber?.verified,
                                                emailVerified: personalData?.data.email?.verified,
                                            });
                                        }
                                    }}
                                >
                                    {personalData?.data.contactNumber?.value ? (
                                        <IconPenRegular size={24} color={skinVars.colors.textLink}></IconPenRegular>
                                    ) : (
                                        <IconAddMoreCircleRegular
                                            size={24}
                                            color={skinVars.colors.textLink}
                                        ></IconAddMoreCircleRegular>
                                    )}
                                </div>
                            )
                        }
                    />
                    <BoxedRow
                        headline={
                            <Tag type={personalData?.data.email?.verified ? 'success' : 'inactive'}>
                                {personalData?.data.email?.verified
                                    ? t('contactVerificationStep1Verified')
                                    : t('contactVerificationStep1NotVerified')}
                            </Tag>
                        }
                        asset={<IconEmailRegular color={skinVars.colors.brand} size={24} />}
                        title={t('personalDataEmail')}
                        subtitle={personalData?.data.email?.value}
                        extra={
                            !personalData?.data.email?.value && (
                                <Text2 medium color={skinVars.colors.textLink}>
                                    {t('personalDataAddEmail')}
                                </Text2>
                            )
                        }
                        right={
                            typeUser !== ETypeUser.PRECLIENT && (
                                <div
                                    className='personal-data__content-boxedRow__icon'
                                    onClick={() => {
                                        if (isLight) {
                                            dispatch(showSnackbar());
                                        } else {
                                            if (isIOS) {
                                                const newWindow = window.open('', 'newWindow');
                                                sessionStorage.setItem('newWindow', newWindow?.name || '');
                                            }
                                            checkEscalation(`/update-personal-data`, 'email', {
                                                mobileVerified: personalData?.data.contactNumber?.verified,
                                                emailVerified: personalData?.data.email?.verified,
                                            });
                                        }
                                    }}
                                >
                                    {personalData?.data.email?.value ? (
                                        <IconPenRegular size={24} color={skinVars.colors.textLink}></IconPenRegular>
                                    ) : (
                                        <IconAddMoreCircleRegular
                                            size={24}
                                            color={skinVars.colors.textLink}
                                        ></IconAddMoreCircleRegular>
                                    )}
                                </div>
                            )
                        }
                    />
                </div>
                {typeUser !== ETypeUser.PRECLIENT && (
                    <>
                        <div className='personal-data__content-titleBankAccount'>
                            <Text1 medium color={skinVars.colors.textSecondary}>
                                {t('personalDataAccount')}
                            </Text1>
                        </div>
                        <div className='personal-data__content-boxedRow'>
                            <BoxedRow
                                asset={<IconCalendarEuroRegular color={skinVars.colors.brand} size={24} />}
                                title={t('personalDataBankAccount')}
                                right={
                                    <div
                                        className='personal-data__content-boxedRow__icon'
                                        onClick={() => {
                                            if (isLight) {
                                                dispatch(showSnackbar());
                                            } else {
                                                if (isIOS) {
                                                    const newWindow = window.open('', 'newWindow');
                                                    sessionStorage.setItem('newWindow', newWindow?.name || '');
                                                }
                                                checkEscalation('/select-bank-account', 'bankAccount', {
                                                    mobileVerified: personalData?.data.contactNumber?.verified,
                                                    emailVerified: personalData?.data.email?.verified,
                                                });
                                            }
                                        }}
                                    >
                                        <IconPenRegular size={24} color={skinVars.colors.textLink}></IconPenRegular>
                                    </div>
                                }
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PersonalData;
